import React, {
    ChangeEvent,
    FC,
    useCallback,
    useMemo,
    useState,
} from 'react';
import { Input, InputContainer } from '@mercell/input-react';
import Button from '@mercell/button-react';
import { Checkbox } from '@mercell/checkbox-react';
import { DatePicker } from '@mercell/date-picker-react';
import { Copy16 } from '@carbon/icons-react';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import cx from 'classnames';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ProgressSpinner } from '../../components/ProgressSpinner';
import {
    getActivityLogById,
    searchActivityLogs,
} from '../../hooks/administrativeHooks/useFetchActivityLogs';
import { useAppSelector } from '../../redux/hooks';
import {
    ActionType,
    ActivityLogObject,
    ActivityLogsModel,
} from '../../types/generated/activityLogsModel';
import { ActivityLogsSearchModel } from '../../types/generated/activityLogsSearchModel';
import ActivityLogSidePanel, { SidePanelMode } from './ActivityLogSidePanel';
import {
    canDiffEntry,
    formatRequestBodyForCopy,
    getActionTypeLabel,
    getOperationLabel,
    isFormActivityEntry,
    parseOperationFromRequestPath,
} from './activityLogUtils';

const MAX_COMPARE_SELECTION = 2;

const ActivityLogTab: FC = () => {
    const [formId, setFormId] = useState('');
    const [objectKind, setObjectKind] = useState<ActivityLogObject>(
        ActivityLogObject.Form
    );
    const [actionType, setActionType] = useState<ActionType>(
        ActionType.HttpRepFormChanged
    );
    const [showAllActions, setShowAllActions] = useState(false);
    const [startDateTime, setStartDateTime] = useState<Date | null>();
    const [endDateTime, setEndDateTime] = useState<Date | null>();
    const [entries, setEntries] = useState<ActivityLogsModel[]>([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
    const [activeRowId, setActiveRowId] = useState<string | null>(null);
    const [panelEntry, setPanelEntry] = useState<ActivityLogsModel | null>(
        null
    );
    const [panelMode, setPanelMode] = useState<SidePanelMode>('empty');
    const [diffOriginal, setDiffOriginal] = useState('');
    const [diffModified, setDiffModified] = useState('');
    const [isPanelLoading, setIsPanelLoading] = useState(false);

    const reduxDateFormat = useAppSelector((state) => state.store.dateFormat);
    const locale = useAppSelector((state) => state.store.locale);
    const { t } = useTranslation(['form-content.logs']);

    const formatTimestamp = useCallback(
        (timestamp: Date | string) => {
            const value =
                typeof timestamp === 'string'
                    ? parseISO(timestamp)
                    : new Date(timestamp);
            return format(value, reduxDateFormat.DateTimeWithZoneShort, {
                locale,
            });
        },
        [locale, reduxDateFormat.DateTimeWithZoneShort]
    );

    const resetPanel = () => {
        setPanelMode('empty');
        setPanelEntry(null);
        setDiffOriginal('');
        setDiffModified('');
        setActiveRowId(null);
    };

    const loadEntryDetail = useCallback(
        (id: string): Promise<ActivityLogsModel> => getActivityLogById(id),
        []
    );

    const showEntryInPanel = async (entry: ActivityLogsModel) => {
        setIsPanelLoading(true);
        try {
            const detail = await loadEntryDetail(entry.id);
            setPanelEntry(detail);
            setActiveRowId(entry.id);

            if (!isFormActivityEntry(detail)) {
                setPanelMode('metadata');
                return;
            }

            setPanelMode('pretty');
        } catch {
            toast.error(
                String(t('form-content.logs:activityLog.loadEntryFailed'))
            );
        } finally {
            setIsPanelLoading(false);
        }
    };

    const onSearch = async () => {
        if (!formId.trim()) {
            return;
        }

        if (
            startDateTime &&
            endDateTime &&
            startDateTime.getTime() > endDateTime.getTime()
        ) {
            setSearchError(
                t('form-content.logs:activityLog.invalidDateRange')
            );
            return;
        }

        setIsSearching(true);
        setSearchError(null);
        resetPanel();
        setSelectedRowIds([]);

        const payload: ActivityLogsSearchModel = {
            affectedObjectId: formId.trim(),
            affectedObject: objectKind,
            actionType: showAllActions ? undefined : actionType,
            startTime: startDateTime ?? undefined,
            endTime: endDateTime ?? undefined,
        };

        try {
            const results = await searchActivityLogs(payload);
            setEntries(results);
            setHasSearched(true);
        } catch {
            setSearchError(
                t('form-content.logs:activityLog.searchFailed')
            );
            setEntries([]);
            setHasSearched(true);
        } finally {
            setIsSearching(false);
        }
    };

    const toggleRowSelection = (id: string) => {
        setSelectedRowIds((current) => {
            if (current.includes(id)) {
                return current.filter((rowId) => rowId !== id);
            }
            if (current.length >= MAX_COMPARE_SELECTION) {
                return [...current.slice(1), id];
            }
            return [...current, id];
        });
    };

    const onCompareSelected = async () => {
        if (selectedRowIds.length !== MAX_COMPARE_SELECTION) {
            return;
        }

        const selectedEntries = entries.filter((entry) =>
            selectedRowIds.includes(entry.id)
        );
        const sorted = [...selectedEntries].sort(
            (a, b) =>
                new Date(a.timestamp).getTime() -
                new Date(b.timestamp).getTime()
        );

        setIsPanelLoading(true);
        try {
            const [older, newer] = await Promise.all([
                loadEntryDetail(sorted[0].id),
                loadEntryDetail(sorted[1].id),
            ]);

            if (!canDiffEntry(older) || !canDiffEntry(newer)) {
                toast.error(
                    String(t('form-content.logs:activityLog.compareNoPayload'))
                );
                return;
            }

            setDiffOriginal(formatRequestBodyForCopy(older.data));
            setDiffModified(formatRequestBodyForCopy(newer.data));
            setPanelEntry(newer);
            setPanelMode('diff');
        } catch {
            toast.error(
                String(t('form-content.logs:activityLog.compareFailed'))
            );
        } finally {
            setIsPanelLoading(false);
        }
    };

    const onCopyPayload = useCallback(
        async (event: React.MouseEvent, entry: ActivityLogsModel) => {
            event.stopPropagation();
            try {
                const detail = await loadEntryDetail(entry.id);
                const payload = formatRequestBodyForCopy(detail.data);
                if (!payload) {
                    toast.error(
                        String(t('form-content.logs:activityLog.noPayload'))
                    );
                    return;
                }
                await navigator.clipboard.writeText(payload);
                toast.success(
                    String(
                        t('form-content.logs:activityLog.copiedToClipboard')
                    )
                );
            } catch {
                toast.error(
                    String(t('form-content.logs:activityLog.copyFailed'))
                );
            }
        },
        [loadEntryDetail, t]
    );

    const canCompareSelection = useMemo(() => {
        if (selectedRowIds.length !== MAX_COMPARE_SELECTION) {
            return false;
        }

        return selectedRowIds.every((id) => {
            const entry = entries.find((item) => item.id === id);
            return entry != null && isFormActivityEntry(entry);
        });
    }, [entries, selectedRowIds]);

    const columns = useMemo<ColumnDef<ActivityLogsModel>[]>(
        () => [
            {
                id: 'select',
                header: '',
                cell: ({ row }) =>
                    isFormActivityEntry(row.original) ? (
                        <Checkbox
                            id={`activity-log-select-${row.original.id}`}
                            checked={selectedRowIds.includes(row.original.id)}
                            onChange={() =>
                                toggleRowSelection(row.original.id)
                            }
                        />
                    ) : null,
                meta: { size: '5%' },
            },
            {
                accessorKey: 'timestamp',
                header: t('form-content.logs:activityLog.timestamp'),
                cell: ({ row }) => formatTimestamp(row.original.timestamp),
                meta: { size: '22%' },
            },
            {
                id: 'operation',
                header: t('form-content.logs:activityLog.operation'),
                cell: ({ row }) =>
                    getOperationLabel(
                        parseOperationFromRequestPath(
                            row.original.requestPath
                        ),
                        t
                    ),
                meta: { size: '15%' },
            },
            {
                accessorKey: 'action',
                header: t('form-content.logs:activityLog.action'),
                cell: ({ row }) =>
                    getActionTypeLabel(row.original.action, t),
                meta: { size: '18%' },
            },
            {
                accessorKey: 'userId',
                header: t('form-content.logs:activityLog.user'),
                meta: { size: '25%' },
            },
            {
                id: 'copy',
                header: t('form-content.logs:activityLog.copy'),
                cell: ({ row }) =>
                    isFormActivityEntry(row.original) ? (
                        <button
                            type="button"
                            className="p-1 hover:bg-hover-grey rounded"
                            title={t(
                                'form-content.logs:activityLog.copyPayload'
                            )}
                            onClick={(event) =>
                                onCopyPayload(event, row.original)
                            }
                        >
                            <Copy16 />
                        </button>
                    ) : null,
                meta: { size: '8%' },
            },
        ],
        [formatTimestamp, onCopyPayload, selectedRowIds, t]
    );

    const table = useReactTable({
        data: entries,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <div>
            <div className="mb-8 space-y-4">
                <div>
                    <p>{t('form-content.logs:activityLog.formId')}</p>
                    <InputContainer>
                        <Input
                            placeholder={t(
                                'form-content.logs:activityLog.formIdPlaceholder'
                            )}
                            value={formId}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setFormId(e.currentTarget.value)
                            }
                        />
                    </InputContainer>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="activity-log-object-kind">
                            {t('form-content.logs:activityLog.objectKind')}
                        </label>
                        <select
                            id="activity-log-object-kind"
                            className="mt-1 w-full border border-alto rounded p-2"
                            value={objectKind}
                            onChange={(e) =>
                                setObjectKind(
                                    Number(e.target.value) as ActivityLogObject
                                )
                            }
                        >
                            <option value={ActivityLogObject.Form}>
                                {t(
                                    'form-content.logs:activityLog.objectKindForm'
                                )}
                            </option>
                            <option value={ActivityLogObject.User}>
                                {t(
                                    'form-content.logs:activityLog.objectKindUser'
                                )}
                            </option>
                        </select>
                    </div>

                    <div>
                        <label htmlFor="activity-log-action-type">
                            {t('form-content.logs:activityLog.actionType')}
                        </label>
                        <select
                            id="activity-log-action-type"
                            className="mt-1 w-full border border-alto rounded p-2"
                            value={actionType}
                            disabled={showAllActions}
                            onChange={(e) =>
                                setActionType(
                                    Number(e.target.value) as ActionType
                                )
                            }
                        >
                            <option value={ActionType.HttpRepFormChanged}>
                                {t(
                                    'form-content.logs:activityLog.actionHttpRepFormChanged'
                                )}
                            </option>
                            <option value={ActionType.FormChanged}>
                                {t(
                                    'form-content.logs:activityLog.actionFormChanged'
                                )}
                            </option>
                            <option value={ActionType.FormDeleted}>
                                {t(
                                    'form-content.logs:activityLog.actionFormDeleted'
                                )}
                            </option>
                            <option value={ActionType.UserUpdated}>
                                {t(
                                    'form-content.logs:activityLog.actionUserUpdated'
                                )}
                            </option>
                            <option value={ActionType.UserDeleted}>
                                {t(
                                    'form-content.logs:activityLog.actionUserDeleted'
                                )}
                            </option>
                        </select>
                    </div>
                </div>

                <Checkbox
                    id="activity-log-show-all-actions"
                    checked={showAllActions}
                    onChange={() => setShowAllActions(!showAllActions)}
                >
                    {t('form-content.logs:activityLog.showAllActions')}
                </Checkbox>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <p>
                            {t(
                                'form-content.logs:activityLog.startDateAndTime'
                            )}
                        </p>
                        <DatePicker
                            onChange={(date) => setStartDateTime(date)}
                            selected={startDateTime}
                            showTimeSelect
                            timeIntervals={5}
                            dateFormat={reduxDateFormat.DateTime}
                        />
                    </div>
                    <div>
                        <p>
                            {t(
                                'form-content.logs:activityLog.endDateAndTime'
                            )}
                        </p>
                        <DatePicker
                            onChange={(date) => setEndDateTime(date)}
                            selected={endDateTime}
                            showTimeSelect
                            timeIntervals={5}
                            dateFormat={reduxDateFormat.DateTime}
                        />
                    </div>
                </div>

                <div className="flex gap-3 items-center">
                    <Button
                        scheme="primary"
                        onClick={onSearch}
                        disabled={!formId.trim() || isSearching}
                    >
                        {t('form-content.logs:activityLog.search')}
                    </Button>
                    <Button
                        scheme="secondary"
                        onClick={onCompareSelected}
                        disabled={!canCompareSelection || isPanelLoading}
                    >
                        {t('form-content.logs:activityLog.compareSelected')}
                    </Button>
                </div>

                {searchError && (
                    <p className="text-red-600 text-sm">{searchError}</p>
                )}
            </div>

            {isSearching ? (
                <div className="flex justify-center py-12">
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="flex gap-4 min-h-[28rem]">
                    <div className="w-[60%] overflow-auto">
                        {hasSearched && entries.length === 0 ? (
                            <p className="text-studio">
                                {t('form-content.logs:activityLog.noResults')}
                            </p>
                        ) : (
                            <div className="overflow-hidden border border-alto md:rounded-default">
                                <table className="min-w-full divide-y divide-concrete table-fixed">
                                    <thead className="bg-minsk">
                                        {table
                                            .getHeaderGroups()
                                            .map((headerGroup) => (
                                                <tr key={headerGroup.id}>
                                                    {headerGroup.headers.map(
                                                        (header) => (
                                                            <th
                                                                key={header.id}
                                                                scope="col"
                                                                className="p-3 text-white title-body text-left"
                                                            >
                                                                {header.isPlaceholder
                                                                    ? null
                                                                    : flexRender(
                                                                          header
                                                                              .column
                                                                              .columnDef
                                                                              .header,
                                                                          header.getContext()
                                                                      )}
                                                            </th>
                                                        )
                                                    )}
                                                </tr>
                                            ))}
                                    </thead>
                                    <tbody className="divide-y divide-concrete bg-white">
                                        {table.getRowModel().rows.map((row) => (
                                            <tr
                                                key={row.id}
                                                className={cx(
                                                    'cursor-pointer hover:bg-hover-grey',
                                                    activeRowId ===
                                                        row.original.id &&
                                                        'bg-snuff'
                                                )}
                                                onClick={(event) => {
                                                    const target =
                                                        event.target as HTMLElement;
                                                    if (
                                                        target.closest(
                                                            'input[type="checkbox"]'
                                                        ) ||
                                                        target.closest('button')
                                                    ) {
                                                        return;
                                                    }
                                                    showEntryInPanel(
                                                        row.original
                                                    );
                                                }}
                                            >
                                                {row
                                                    .getVisibleCells()
                                                    .map((cell) => (
                                                        <td
                                                            key={cell.id}
                                                            className="p-3 text-black title-body break-all text-[14px] font-medium"
                                                        >
                                                            {flexRender(
                                                                cell.column
                                                                    .columnDef
                                                                    .cell,
                                                                cell.getContext()
                                                            )}
                                                        </td>
                                                    ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="w-[40%] border border-alto rounded-default min-h-[24rem]">
                        <ActivityLogSidePanel
                            mode={panelMode}
                            selectedEntry={panelEntry}
                            diffOriginal={diffOriginal}
                            diffModified={diffModified}
                            isLoading={isPanelLoading}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActivityLogTab;
